import React, { useState } from "react";
import { useCart } from "./CartContext";
import Cart from "./Cart";
import {loadStripe} from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { COUNTRY_NAMES } from "./countryNames";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_live_51R79ejJQgQGbk1p5YdHddxoDbVG6Qt0k7Asvd7JmO3IcfXb8bgZOHobP5G5LWG636sLFGa9aKbpvbqORv6o1ge2700LuLe5VXC");

// AusPost international and domestic rates (500g–1kg) as of June 2025
const SHIPPING_RATES = [
  {
    countries: ["AU"],
    label: "Australia (Standard)",
    rate: 1500, // $15.00 AUD
    description: "Domestic Standard (500g–1kg)",
    id: "shipping_au_standard",
    express: false
  },
  {
    countries: ["AU"],
    label: "Australia (Express)",
    rate: 2500, // $25.00 AUD
    description: "Domestic Express (500g–1kg)",
    id: "shipping_au_express",
    express: true
  },
  {
    countries: ["NZ"],
    label: "Zone 1: New Zealand",
    rate: 2575, // $25.75 AUD
    description: "Standard (500g–1kg)",
    id: "shipping_nz"
  },
  {
    countries: ["CN", "JP", "KR", "SG", "HK", "MY", "PH", "TH", "ID", "VN", "TW", "IN", "PK", "BD", "LK", "NP", "KH", "LA", "MM", "MN", "MO", "BN", "TL", "PG", "FJ", "SB", "VU", "NC", "PF", "WS", "TO", "TV", "KI"],
    label: "Zone 2: Asia Pacific",
    rate: 3720, // $37.20 AUD
    description: "Standard (500g–1kg)",
    id: "shipping_asia_pacific"
  },
  {
    countries: ["US", "CA"],
    label: "Zone 3: USA and Canada",
    rate: 4115, // $41.15 AUD
    description: "Standard (500g–1kg)",
    id: "shipping_usa_canada"
  },
  {
    countries: ["GB", "FR", "DE", "IT", "ES", "NL", "BE", "AT", "CH", "DK", "SE", "NO", "FI", "IE", "PT", "GR", "PL", "CZ", "HU", "SK", "SI", "HR", "RO", "BG", "EE", "LV", "LT", "LU", "LI", "MC", "SM", "VA", "IS", "UA", "RU", "BY", "MD", "RS", "ME", "MK", "AL", "BA"],
    label: "Zone 4: UK and Europe",
    rate: 4710, // $47.10 AUD
    description: "Standard (500g–1kg)",
    id: "shipping_uk_europe"
  },
  {
    countries: ["ALL_OTHER"], // fallback for rest of world
    label: "Zone 5: Rest of World",
    rate: 5900, // $59.00 AUD
    description: "Standard (500g–1kg)",
    id: "shipping_rest_world"
  }
];

function getShippingOptionsForCountry(country) {
  // Return all matching options for the country (for AU, both standard and express)
  const matches = SHIPPING_RATES.filter(zone => zone.countries.includes(country));
  if (matches.length > 0) return matches;
  // Default to 'Rest of World' if not found
  return [SHIPPING_RATES[SHIPPING_RATES.length - 1]];
}

export default function CartDrawer({ open, onClose }) {
  const [step, setStep] = useState("cart");
  const { cart } = useCart();
  const cartItems = Object.entries(cart);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShippingId, setSelectedShippingId] = useState("");
  const [shippingZone, setShippingZone] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);

  // Calculate cart subtotal
  const cartSubtotal = cartItems.reduce((sum, [id, quantity]) => {
    const product = JSON.parse(localStorage.getItem(`product_${id}`));
    return sum + (product && product.price ? product.price * quantity : 0);
  }, 0);

  // Update shipping options and selected shipping when country changes
  React.useEffect(() => {
    if (selectedCountry) {
      const options = getShippingOptionsForCountry(selectedCountry);
      setShippingOptions(options);
      setSelectedShippingId(options[0].id); // Default to first option
      setShippingZone(options[0]);
    } else {
      setShippingOptions([]);
      setSelectedShippingId("");
      setShippingZone(null);
    }
  }, [selectedCountry]);

  // Update shippingZone when selectedShippingId changes
  React.useEffect(() => {
    if (shippingOptions.length > 0 && selectedShippingId) {
      const found = shippingOptions.find(opt => opt.id === selectedShippingId);
      setShippingZone(found || shippingOptions[0]);
    }
  }, [selectedShippingId, shippingOptions]);

  // Fetch client secret for embedded checkout
  const fetchClientSecret = async () => {
    const line_items = cartItems.map(([id, quantity]) => {
      const product = JSON.parse(localStorage.getItem(`product_${id}`));
      if (!product || !product.priceId) return null;
      return {
        price: product.priceId,
        quantity,
      };
    }).filter(Boolean);
    // Add shipping as a custom line item
    if (shippingZone) {
      line_items.push({
        price_data: {
          currency: "aud", // AUD for Australia Post
          product_data: {
            name: `Shipping (${shippingZone.label})`,
            description: shippingZone.description
          },
          unit_amount: shippingZone.rate,
        },
        quantity: 1,
      });
    }
    const res = await fetch("https://stripe-shop-backend-gsck.onrender.com/create-checkout-session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ line_items }),
    });
    const data = await res.json();
    return data.clientSecret;
  };

  // After successful checkout, mark all purchased products as sold
  const markProductsSold = async () => {
    for (const [id] of cartItems) {
      await fetch("https://stripe-shop-backend-gsck.onrender.com/mark-sold", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: id })
      });
    }
  };

  const handleProceedToCheckout = async () => {
    setLoading(true);
    const secret = await fetchClientSecret();
    setClientSecret(secret);
    setStep("checkout");
    setLoading(false);
    // Optionally, you could call markProductsSold() after payment confirmation instead
  };

  const handleBackToCart = () => {
    setStep("cart");
  };

  return (
    <div className={`cart-drawer${open ? " open" : ""}`}> 
      <div className="cart-drawer-backdrop" onClick={onClose} />
      <div className="cart-drawer-panel">
        <button className="cart-drawer-close" onClick={onClose}>&times;</button>
        {step === "cart" && (
          <>
            <Cart />
            {/* Shipping selector UI */}
            <div style={{margin: "18px 0 8px 0"}}>
              <label htmlFor="country-select" style={{fontWeight: 600, color: "#ffe066"}}>Shipping Destination:</label>
              <select
                id="country-select"
                value={selectedCountry}
                onChange={e => setSelectedCountry(e.target.value)}
                style={{marginLeft: 12, padding: 6, borderRadius: 6, fontWeight: 500}}
              >
                <option value="">Select country...</option>
                {Array.from(new Set(SHIPPING_RATES.flatMap(z => z.countries)))
                  .filter(code => code !== "ALL_OTHER")
                  .sort((a, b) => {
                    const nameA = COUNTRY_NAMES[a] || a;
                    const nameB = COUNTRY_NAMES[b] || b;
                    return nameA.localeCompare(nameB);
                  })
                  .map(code => (
                    <option key={code} value={code}>{COUNTRY_NAMES[code] || code}</option>
                  ))}
              </select>
              {shippingOptions.length > 0 && (
                <div style={{marginTop: 8}}>
                  {shippingOptions.length > 1 ? (
                    <>
                      <label htmlFor="shipping-option-select" style={{fontWeight: 500, color: "#ffe066"}}>Shipping Option:</label>
                      <select
                        id="shipping-option-select"
                        value={selectedShippingId}
                        onChange={e => setSelectedShippingId(e.target.value)}
                        style={{marginLeft: 12, padding: 6, borderRadius: 6, fontWeight: 500}}
                      >
                        {shippingOptions.map(opt => (
                          <option key={opt.id} value={opt.id}>{opt.label} - ${ (opt.rate / 100).toFixed(2) }</option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <div style={{color: "#ff61a6", fontWeight: 600}}>
                      Shipping: ${ (shippingOptions[0].rate / 100).toFixed(2) } ({shippingOptions[0].label})
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="cart-total-row">
              <span className="cart-total-label">Total:</span>
              <span className="cart-total-value">
                ${ ((cartSubtotal + (shippingZone ? shippingZone.rate : 0)) / 100).toFixed(2) }
              </span>
            </div>
            <button
              className="cart-checkout-btn"
              style={{width: "100%", marginTop: 16}}
              onClick={handleProceedToCheckout}
              disabled={cartItems.length === 0 || loading || !selectedCountry}
            >
              {loading ? "Loading..." : "Proceed to Checkout"}
            </button>
          </>
        )}
        {step === "checkout" && clientSecret && (
          <>
            <button onClick={handleBackToCart} style={{marginBottom: 12}}>&larr; Back to Cart</button>
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{clientSecret}}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </>
        )}
      </div>
    </div>
  );
}
